// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import type { TourRoundSummary, SidebarSelection } from '../types';
import SidebarItem from './SidebarItem';
import RoundSidebarSection from './RoundSidebarSection';

type Props = {
  tourName: string;
  playerCount: number;
  gameCount: number;
  rounds: TourRoundSummary[];
  selection: SidebarSelection;
  expandedRoundId: string | null;
  onSelect: (selection: SidebarSelection) => void;
  onExpandRound: (roundId: string | null) => void;
  onAddRound: () => void;
  onDeleteRound: (roundId: string) => void;
};

export default function Sidebar({
  playerCount,
  gameCount,
  rounds,
  selection,
  expandedRoundId,
  onSelect,
  onExpandRound,
  onAddRound,
  onDeleteRound,
}: Props) {
  const handleSelect = (sel: SidebarSelection) => {
    onSelect(sel);
    if ('roundId' in sel) {
      onExpandRound(sel.roundId);
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {/* Tour Settings */}
        <Text style={styles.sectionLabel}>Tour Settings</Text>
        <SidebarItem
          label="Tour basics"
          active={selection.kind === 'tour-name'}
          onPress={() => handleSelect({ kind: 'tour-name' })}
        />
        <SidebarItem
          label="Players"
          count={playerCount}
          active={selection.kind === 'tour-players'}
          onPress={() => handleSelect({ kind: 'tour-players' })}
        />
        <SidebarItem
          label="Games"
          count={gameCount}
          active={selection.kind === 'tour-games'}
          onPress={() => handleSelect({ kind: 'tour-games' })}
        />

        {/* Divider */}
        <View style={styles.divider} />

        {/* Rounds */}
        <Text style={styles.sectionLabel}>Rounds</Text>
        {rounds.map((round, index) => (
          <RoundSidebarSection
            key={round.id}
            round={round}
            index={index}
            expanded={expandedRoundId === round.id}
            selection={selection}
            onToggleExpand={() => onExpandRound(expandedRoundId === round.id ? null : round.id)}
            onSelect={handleSelect}
            onDelete={() => onDeleteRound(round.id)}
          />
        ))}

        {/* Add Round */}
        <Pressable style={styles.addRoundButton} onPress={onAddRound}>
          <Text style={styles.addRoundIcon}>+</Text>
          <Text style={styles.addRoundLabel}>Add round</Text>
        </Pressable>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.panel,
    borderRightWidth: 1,
    borderRightColor: appColors.border.default,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 12,
    paddingTop: 8,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 4,
  },
  divider: {
    height: 1,
    backgroundColor: appColors.border.default,
    marginVertical: 12,
    marginHorizontal: 8,
  },
  addRoundButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 8,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.base,
  },
  addRoundIcon: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  addRoundLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
});
